/**
 * Veda Agro — Legacy Invoices list page (record-only imports). Ticket AI-4voo.
 *
 * Lists imported external/ERP sale invoices, filterable by buyer / SO / text.
 * "Import invoice" opens LegacyInvoiceImportForm. Row click expands the line
 * items + shows the linked SO and (if present) a PDF-key view link.
 * NO GST/accounting — purely reference records. Isolated new file.
 */
import { useState, useEffect, useCallback } from 'react';
import { Plus, Loader2, FileText, FileX2, ExternalLink } from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';
import { LegacyInvoiceImportForm } from './LegacyInvoiceImportForm';

interface LegacyItem {
  id: string;
  description: string;
  hsnSac?: string | null;
  quantity: number | string;
  rate: number | string;
  gstRate: number | string;
  amount: number | string;
}

interface LegacyInvoice {
  id: string;
  externalNumber: string;
  irn?: string | null;
  ackNo?: string | null;
  ewayBillNo?: string | null;
  vehicleNo?: string | null;
  invoiceDate: string;
  salesOrderId?: string | null;
  accountId: string;
  buyerName?: string | null;
  buyerGstin?: string | null;
  subtotal: number | string;
  totalGst: number | string;
  total: number | string;
  pdfKey?: string | null;
  status: string;
  createdAt?: string;
  items?: LegacyItem[];
}

interface AccountRow { id: string; name: string }

const inr = (n: number | string) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export function LegacyInvoicesPage() {
  const [rows, setRows] = useState<LegacyInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [buyerFilter, setBuyerFilter] = useState('');
  const [search, setSearch] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {};
      if (buyerFilter) params.accountId = buyerFilter;
      if (search.trim()) params.search = search.trim();
      const res = await api.get('/veda-agro/legacy-invoices', { params });
      setRows(res.data.data ?? []);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [buyerFilter, search]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    api.get('/accounts?type=customer&limit=200')
      .then((r) => setAccounts(r.data.data?.accounts ?? r.data.data ?? []))
      .catch(() => {});
  }, []);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-emerald-900">Legacy Invoices</h1>
          <p className="text-sm text-gray-500">External / ERP sale invoices imported for reference (no GST posting).</p>
        </div>
        <button onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
          <Plus size={16} /> Import invoice
        </button>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-3">
        <select value={buyerFilter} onChange={(e) => setBuyerFilter(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
          <option value="">All buyers</option>
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search invoice no. / IRN / e-Way bill"
          className="min-w-[240px] flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm" />
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 py-16 text-gray-400">
          <FileX2 size={32} />
          <p className="mt-2 text-sm">No legacy invoices imported yet.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-100">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2.5">Invoice no.</th>
                <th className="px-4 py-2.5">Date</th>
                <th className="px-4 py-2.5">Buyer</th>
                <th className="px-4 py-2.5">e-Way bill</th>
                <th className="px-4 py-2.5 text-right">Total</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((r) => (
                <>
                  <tr key={r.id} className="cursor-pointer hover:bg-emerald-50/40"
                    onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                    <td className="px-4 py-3 font-medium text-emerald-900">{r.externalNumber}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(r.invoiceDate)}</td>
                    <td className="px-4 py-3 text-gray-700">{r.buyerName ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{r.ewayBillNo ?? '—'}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">{inr(r.total)}</td>
                    <td className="px-4 py-3 text-right">
                      {r.pdfKey && (
                        <a href={`/api/v1/files/${encodeURIComponent(r.pdfKey)}`} target="_blank" rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline">
                          <FileText size={13} /> PDF
                        </a>
                      )}
                    </td>
                  </tr>
                  {expanded === r.id && (
                    <tr key={`${r.id}-exp`} className="bg-gray-50/60">
                      <td colSpan={6} className="px-4 py-3">
                        <div className="mb-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-600">
                          {r.irn && <span>IRN: <span className="font-mono text-gray-800">{r.irn}</span></span>}
                          {r.ackNo && <span>Ack: {r.ackNo}</span>}
                          {r.vehicleNo && <span>Vehicle: {r.vehicleNo}</span>}
                          {r.buyerGstin && <span>Buyer GSTIN: {r.buyerGstin}</span>}
                          {r.salesOrderId && (
                            <a href={`/veda-agro/orders/so/${r.salesOrderId}`}
                              className="inline-flex items-center gap-1 text-emerald-700 hover:underline">
                              <ExternalLink size={12} /> Linked SO
                            </a>
                          )}
                          <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[11px] uppercase text-gray-600">{r.status}</span>
                        </div>
                        <table className="w-full text-xs">
                          <thead className="text-left text-gray-400">
                            <tr>
                              <th className="py-1">Description</th>
                              <th className="py-1">HSN/SAC</th>
                              <th className="py-1 text-right">Qty</th>
                              <th className="py-1 text-right">Rate</th>
                              <th className="py-1 text-right">GST%</th>
                              <th className="py-1 text-right">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(r.items ?? []).map((it) => (
                              <tr key={it.id} className="text-gray-700">
                                <td className="py-1">{it.description}</td>
                                <td className="py-1">{it.hsnSac ?? '—'}</td>
                                <td className="py-1 text-right">{Number(it.quantity)}</td>
                                <td className="py-1 text-right">{inr(it.rate)}</td>
                                <td className="py-1 text-right">{Number(it.gstRate)}%</td>
                                <td className="py-1 text-right">{inr(it.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <LegacyInvoiceImportForm
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); fetchRows(); }}
        />
      )}
    </div>
  );
}
